import { useEffect, useRef, useState, useMemo, useCallback } from 'react'
import axios from 'axios'
import Container from '@mui/material/Container'
import { useAuthContext } from '../../hooks/useAuthContext'
import {
  IMMEDIATE_ACTION,
  HIDDEN_ACTIVITY_TYPES,
  NUMBER_OF_TASKS_AND_QUERIES_ANSWERED_AT_A_TIME_BY_USER,
  DROPDOWN_DEFAULT_TEXT,
} from '../../utility/constants'
import ActivityFilter from '../../components/Dashboard/ActivityFilter'
import Switch from '@mui/material/Switch'
import {
  Box,
  Divider,
  Tab,
  Tabs,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  SwipeableDrawer,
  Badge,
  Tooltip,
} from '@mui/material'
import { ACTIVITY_TYPES } from '../../utility/constants'
import { Autocomplete, TextField, CircularProgress } from '@mui/material'
import DelegateBox from '../../components/Dashboard/DelegateBox'
import { toast } from 'react-toastify'
import { useResponsiveTypography } from '../../utility/responsiveTypography'
import MyTaskTableView from '../../components/Dashboard/MyTaskTableView'
import MyTaskCardView from '../../components/Dashboard/MyTaskCardView'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import FilterListIcon from '@mui/icons-material/FilterList'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useMyTasks } from '../../context/MyTasksContext'
import { userTrackingStorage } from '../../utility/storageUtils'
import { useBadges } from '../../context/BadgeContext'
import {
  useDrawerState,
  DRAWER_WIDTH_OPENED,
  DRAWER_WIDTH_CLOSED,
} from '../../context/DrawerContext'
import { FilterListOffOutlined } from '@mui/icons-material'

const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL
const BREAKPOINTS = { default: 4, 1100: 3, 700: 1 }

export default function MyTasks() {
  const {
    allTask,
    setAllTask,
    visibleData,
    hiddenTaskIds,
    filterCategories,
    isActiveTasks,
    isCardView,
    switchActiveTasks,
    switchCardView,
    filter,
    setFilterAndResetRefs,
    selectedRefNums,
    setSelectedRefNums,
    actionRequired,
    setActionRequired,
    nextCursor,
    hasMore,
    initialLoading,
    isLoading,
    fetchData,
    ensureData,
    resetForFilterChange,
    deleteTaskLocally,
    saveScrollPosition,
    restoreScrollPosition,
    abortRef,
    allCategories,
    ensureCategories,
  } = useMyTasks()

  const { open, isLargeScreen } = useDrawerState()
  const { decrementTaskBadge } = useBadges()

  const [activityType, setActivityType] = useState(ACTIVITY_TYPES.REVIEW)
  const activityTypeHydratedRef = useRef(false)

  const [tabSwitchLoading, setTabSwitchLoading] = useState(false)
  const [teamDetails, setTeamDetails] = useState([])
  const [task, setTask] = useState(null)
  const [processReferenceNumber, setProcessReferenceNumber] = useState(null)
  const [isDelegateOpen, setIsDelegateOpen] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState({
    id: null,
    name: DROPDOWN_DEFAULT_TEXT,
  })
  const [filterResetLoading, setFilterResetLoading] = useState(false)
  const [isMounting, setIsMounting] = useState(true)
  const [subheaderHeight, setSubheaderHeight] = useState(54)
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)

  const observerRef = useRef(null)
  const subheaderRef = useRef(null)

  const { user } = useAuthContext()
  const { cardIcons } = useResponsiveTypography()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'))

  const axiosOptions = useMemo(
    () => ({ headers: { Authorization: `Bearer ${user?.token}` } }),
    [user?.token],
  )

  useEffect(() => {
    const el = subheaderRef.current
    if (!el) return
    const observer = new ResizeObserver(([entry]) => {
      setSubheaderHeight(entry.contentRect.height)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!user?.id || activityTypeHydratedRef.current) return
    activityTypeHydratedRef.current = true
    const saved = userTrackingStorage.getForUser(user.id)
    if (saved?.activityType) setActivityType(saved.activityType)
  }, [user?.id])

  useEffect(() => {
    return () => {
      abortRef.current?.abort()
    }
  }, [])

  useEffect(() => {
    if (!user) {
      setIsMounting(false)
      return
    }
    const cacheHit = ensureData(isActiveTasks, isCardView)
    if (cacheHit) {
      requestAnimationFrame(() => {
        setIsMounting(false)
        restoreScrollPosition()
      })
    } else {
      setIsMounting(false)
      fetchData(null, true, isActiveTasks, isCardView, filter, selectedRefNums)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.token, isActiveTasks, isCardView])

  const categoriesFetchedForRef = useRef(null)
  useEffect(() => {
    if (!user) return
    if (categoriesFetchedForRef.current === user.token) return
    categoriesFetchedForRef.current = user.token
    ensureCategories()
  }, [user?.token])

  const teamFetchedForRef = useRef(null)
  useEffect(() => {
    if (!user) return
    if (teamFetchedForRef.current === user.token) return
    teamFetchedForRef.current = user.token
    getTeamData()
  }, [user?.token])

  const drawerOffset = isLargeScreen ? (open ? DRAWER_WIDTH_OPENED : DRAWER_WIDTH_CLOSED) : 0

  async function getTeamData() {
    try {
      const filteredList = (
        await axios.get(
          `${NODE_BASE_URL}/data-entry/process-data/get-all-activity-team-map`,
          axiosOptions,
        )
      ).data
      const userMap = new Map()
      filteredList.forEach((item) => {
        if (!userMap.has(item.user_id)) {
          userMap.set(item.user_id, { ...item, team_ids: [item.team_id] })
        } else {
          userMap.get(item.user_id).team_ids.push(item.team_id)
        }
      })
      setTeamDetails(Array.from(userMap.values()))
    } catch (error) {
      console.error('Error fetching team data:', error)
    }
  }

  useEffect(() => {
    setSelectedCategory((prev) => ({ ...prev, id: filter.categoryId }))
  }, [filter.categoryId])

  async function deleteTask(task) {
    try {
      const processId = task.process_id
      await axios.delete(`${NODE_BASE_URL}/data-entry/process-data/${processId}`, axiosOptions)
      deleteTaskLocally(processId)
      decrementTaskBadge(NUMBER_OF_TASKS_AND_QUERIES_ANSWERED_AT_A_TIME_BY_USER)
      toast.success(`${task.process_reference} has been deleted successfully.`)
    } catch (error) {
      toast.error(error.response.data.error)
    }
  }

  const uniqueTasks = useMemo(() => {
    if (filter?.categoryId != null) {
      return allTask.filter((task) => !hiddenTaskIds?.has?.(task.process_id))
    }
    const seen = new Set()
    return allTask.filter((task) => {
      if (seen.has(task.process_id)) return false
      seen.add(task.process_id)
      return !hiddenTaskIds?.has?.(task.process_id)
    })
  }, [allTask, hiddenTaskIds, filter?.categoryId])

  const filteredBaseTasks = useMemo(() => {
    return uniqueTasks
      .filter((task) => !HIDDEN_ACTIVITY_TYPES.includes(task.activity_type))
      .filter((task) => {
        if (!isActiveTasks) return true
        if (actionRequired === IMMEDIATE_ACTION.immediate_action_required)
          return task?.is_immediate_action_required === true
        if (actionRequired === IMMEDIATE_ACTION.no_immediate_action_required)
          return task?.is_immediate_action_required === false
        return true
      })
  }, [uniqueTasks, isActiveTasks, actionRequired])

  const scopedProcessRefNums = useMemo(() => {
    let base = filteredBaseTasks
    if (filter.activityTemplateId) {
      base = base.filter((t) => t.activity_template_id == filter.activityTemplateId)
    } else if (filter.processTemplateId) {
      base = base.filter((t) => t.process_template_id == filter.processTemplateId)
    } else if (filter.categoryId) {
      base = base.filter((t) => t.process_category_id == filter.categoryId)
    }
    const seen = new Set()
    return base
      .filter(
        (t) =>
          t.process_reference && !seen.has(t.process_reference) && seen.add(t.process_reference),
      )
      .map((t) => ({ label: t.process_reference }))
  }, [filteredBaseTasks, filter])

  useEffect(() => {
    if (
      !initialLoading &&
      selectedRefNums.length > 0 &&
      scopedProcessRefNums.length > 0 &&
      allTask.length > 0
    ) {
      const validLabels = new Set(scopedProcessRefNums.map((r) => r.label))
      const stillValid = selectedRefNums.filter((r) => validLabels.has(r.label ?? r))
      if (stillValid.length !== selectedRefNums.length) setSelectedRefNums(stillValid)
    }
  }, [initialLoading, scopedProcessRefNums, allTask.length])

  useEffect(() => {
    if (!observerRef.current || !hasMore || isLoading) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          fetchData(nextCursor, true, isActiveTasks, isCardView, filter, selectedRefNums)
        }
      },
      { root: null, rootMargin: '200px', threshold: 0 },
    )
    observer.observe(observerRef.current)
    return () => {
      observer.disconnect()
    }
  }, [isActiveTasks, isCardView, hasMore, isLoading, nextCursor, allTask.length])

  const selectedRefSet = useMemo(() => {
    return new Set(selectedRefNums.map((r) => r.label ?? r))
  }, [selectedRefNums])

  const filterTasks = useCallback(
    (tasks, filter) => {
      let result = tasks
      if (filter.activityTemplateId) {
        result = result.filter((it) => it.activity_template_id == filter.activityTemplateId)
      } else if (filter.processTemplateId) {
        result = result.filter((it) => it.process_template_id == filter.processTemplateId)
      } else if (filter.categoryId) {
        result = result.filter((it) => it.process_category_id == filter.categoryId)
      }
      if (selectedRefSet.size > 0) {
        result = result.filter((it) => selectedRefSet.has(it.process_reference))
      }
      return result
    },
    [selectedRefSet],
  )

  const onNavigate = useCallback(
    (navCallback) => {
      if (typeof navCallback === 'function') {
        saveScrollPosition()
        navCallback()
      }
    },
    [saveScrollPosition],
  )

  const displayedTasks = useMemo(
    () => filterTasks(filteredBaseTasks, filter),
    [filteredBaseTasks, filter, filterTasks],
  )

  const immediateTasks = useMemo(
    () => displayedTasks.filter((t) => t.is_immediate_action_required),
    [displayedTasks],
  )
  const nonImmediateTasks = useMemo(
    () => displayedTasks.filter((t) => !t.is_immediate_action_required),
    [displayedTasks],
  )

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (filter.categoryId) count += 1
    if (filter.processTemplateId) count += 1
    if (filter.activityTemplateId) count += 1
    if (selectedRefNums?.length > 0) count += 1
    if (isActiveTasks && actionRequired !== IMMEDIATE_ACTION.all) count += 1
    return count
  }, [filter, selectedRefNums, isActiveTasks, actionRequired])

  const handleFilterChange = (newFilter) => {
    if (actionRequired === IMMEDIATE_ACTION.no_immediate_action_required) {
      setActionRequired(IMMEDIATE_ACTION.all)
    }
    setFilterResetLoading(true)
    setTimeout(() => {
      if (!isActiveTasks && isCardView) {
        resetForFilterChange()
        setFilterAndResetRefs(newFilter)
        fetchData(null, true, isActiveTasks, isCardView, newFilter, [], true)
      } else {
        setFilterAndResetRefs(newFilter)
      }
      setFilterResetLoading(false)
    }, 0)
  }

  const onActivityTypeChange = (_, newValue) => {
    if (newValue === activityType) return
    setTabSwitchLoading(true)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setActivityType(newValue)
        setTimeout(() => setTabSwitchLoading(false), 100)
      })
    })
  }

  const hasNonImmediateTasks = useMemo(() => {
    if (!isActiveTasks) return false
    const tasksWithoutImmediateFilter = uniqueTasks.filter(
      (task) => !HIDDEN_ACTIVITY_TYPES.includes(task.activity_type),
    )
    return filterTasks(tasksWithoutImmediateFilter, filter).some(
      (task) => !task.is_immediate_action_required,
    )
  }, [isActiveTasks, uniqueTasks, filter, filterTasks])

  const toggleFilterDrawer = (nextOpen) => (event) => {
    if (event?.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) return
    setFilterDrawerOpen(nextOpen)
  }

  // Mobile toggle handlers 
  const handleMobileActiveToggle = useCallback(() => {
    setFilterResetLoading(true)
    setTimeout(() => {
      switchActiveTasks(!isActiveTasks)
      setFilterResetLoading(false)
    }, 0)
  }, [isActiveTasks, switchActiveTasks])

  const handleMobileCardViewToggle = useCallback(() => {
    setFilterResetLoading(true)
    setTimeout(() => {
      switchCardView(!isCardView)
      setFilterResetLoading(false)
    }, 0)
  }, [isCardView, switchCardView])

  const switchSx = (checked) => ({
    '& .MuiSwitch-switchBase': {
      padding: 1,
      '&.Mui-checked': {
        color: '#6C47E4',
        '& + .MuiSwitch-track': { backgroundColor: '#E1D9FB' },
      },
    },
    '& .MuiSwitch-thumb': { backgroundColor: checked ? '#6C47E4' : '#FFFFFF' },
    '& .MuiSwitch-track': {
      backgroundColor: checked ? '#E1D9FB' : '#D9D9D9',
      opacity: 1,
      borderRadius: 20 / 2,
    },
  })

  // Shared Controls 
  const activityFilterControl = allTask?.length > 0 && (
    <ActivityFilter
      className="tasktab"
      tasks={uniqueTasks}
      categories={allCategories}
      filter={filter}
      applyFilter={(newFilter) => {
        handleFilterChange(newFilter)
      }}
      id={JSON.stringify(filter)}
      filterCategories={filterCategories}
      isCardView={isCardView}
      isActiveTasks={isActiveTasks}
    />
  )

  const immediateActionControl = isActiveTasks && hasNonImmediateTasks && (
    <FormControl
      size="small"
      sx={{ minWidth: { xs: '100%', sm: '150px' }, height: { sm: '34.25px' } }}
    >
      <InputLabel>Immediate Action</InputLabel>
      <Select
        value={actionRequired}
        onChange={(e) => {
          setFilterResetLoading(true)
          const val = e.target.value
          setTimeout(() => {
            setActionRequired(val)
            setFilterResetLoading(false)
          }, 0)
        }}
        label="Immediate Action"
        MenuProps={{ PaperProps: { sx: { mt: 1 } } }}
        sx={{
          borderRadius: '16px',
          '& .MuiOutlinedInput-notchedOutline': {
            borderRadius: '16px',
          },
        }}
      >
        {Object.entries(IMMEDIATE_ACTION).map(([key, value], index) => (
          <MenuItem key={index} value={value}>
            {value}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )

  const referenceNumberControl = isCardView && (
    <Box sx={{ width: { xs: '100%', sm: 'auto' }, maxWidth: '100%' }}>
      <Autocomplete
        options={scopedProcessRefNums}
        value={selectedRefNums}
        onChange={(_, value) => {
          if (!isActiveTasks && isCardView) {
            setFilterResetLoading(true)
            setTimeout(() => {
              resetForFilterChange()
              setSelectedRefNums(value)
              fetchData(null, true, isActiveTasks, isCardView, filter, value, true)
              setFilterResetLoading(false)
            }, 0)
          } else {
            setFilterResetLoading(true)
            setTimeout(() => {
              setSelectedRefNums(value)
              setFilterResetLoading(false)
            }, 0)
          }
        }}
        multiple
        renderTags={(value) => {
          if (!value.length) return null
          return (
            <Chip
              label={
                value.length === 1
                  ? value[0].label || value[0]
                  : `${value[0].label || value[0]} +${value.length - 1}`
              }
              size="small"
              sx={{
                maxWidth: 150,
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                },
                '& .MuiChip-deleteIcon': { marginLeft: '4px' },
              }}
            />
          )
        }}
        sx={{
          width: { xs: '100%', sm: 180 },
          maxWidth: '100%',
          boxSizing: 'border-box',
          '& .MuiOutlinedInput-root': { borderRadius: '16px', minHeight: '1.4375em' },
          '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap !important', overflow: 'hidden' },
          '& .MuiChip-root': { height: '24px', fontSize: '14px', maxWidth: '150px' },
          '& .MuiAutocomplete-input': { minWidth: 0 },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Reference Number"
            size="small"
            fullWidth
            sx={{
              '& .MuiInputLabel-root': { color: 'rgba(0, 0, 0, 0.6)', fontWeight: 400 },
              '& .MuiInputLabel-root.Mui-focused': { color: 'rgba(0, 0, 0, 0.6)' },
            }}
          />
        )}
      />
    </Box>
  )

  // Desktop toggles
  const togglesControl = (
    <Box
      className="toggle-groupbtn"
      sx={{ display: 'flex', flexDirection: 'row', width: 'auto', gap: 0, p: 0 }}
    >
      <Box className="d-flex align-item-center" sx={{ display: 'flex', alignItems: 'center' }}>
        <Switch
          checked={isActiveTasks}
          onChange={() => switchActiveTasks(!isActiveTasks)}
          sx={switchSx(isActiveTasks)}
        />
        <Typography
          className="fontsize12 textPrimary fontWeight700"
          variant="body2"
          style={{ whiteSpace: 'nowrap', color: 'text.primary' }}
        >
          Active Task
        </Typography>
      </Box>
      <Box className="d-flex align-item-center" sx={{ display: 'flex', alignItems: 'center' }}>
        <Switch
          checked={isCardView}
          onChange={() => switchCardView(!isCardView)}
          sx={switchSx(isCardView)}
        />
        <Typography
          className="fontsize12 textPrimary fontWeight700"
          variant="body2"
          style={{ whiteSpace: 'nowrap', color: 'text.primary' }}
        >
          Card View
        </Typography>
      </Box>
    </Box>
  )

  // Mobile drawer toggles 
  const mobileTogglesControl = (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
          Active Task
        </Typography>
        <Switch
          checked={isActiveTasks}
          onChange={handleMobileActiveToggle}
          disabled={filterResetLoading}
          sx={switchSx(isActiveTasks)}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
          Card View
        </Typography>
        <Switch
          checked={isCardView}
          onChange={handleMobileCardViewToggle}
          disabled={filterResetLoading}
          sx={switchSx(isCardView)}
        />
      </Box>
    </Box>
  )

  const activityTabsControl = !isCardView && (
    <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
      <Tabs
        value={activityType}
        onChange={onActivityTypeChange}
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
        TabIndicatorProps={{ style: { display: 'none' } }}
        className="custom-tabs-container"
      >
        <Tab
          icon={<EditOutlinedIcon sx={{ fontSize: '18px', marginRight: '4px !important' }} />}
          iconPosition="start"
          label={ACTIVITY_TYPES.DATA_ENTRY}
          value={ACTIVITY_TYPES.DATA_ENTRY}
          className={`custom-tab ${activityType === ACTIVITY_TYPES.DATA_ENTRY ? 'active-tab' : ''}`}
        />
        <Tab
          icon={<AssignmentOutlinedIcon sx={{ fontSize: '18px', marginRight: '4px !important' }} />}
          iconPosition="start"
          label={ACTIVITY_TYPES.REVIEW}
          value={ACTIVITY_TYPES.REVIEW}
          className={`custom-tab ${activityType === ACTIVITY_TYPES.REVIEW ? 'active-tab' : ''}`}
        />
      </Tabs>
    </Box>
  )

  const clearAllFilters = useCallback(() => {
    if (activeFilterCount === 0) return

    setFilterResetLoading(true)
    setTimeout(() => {
      const emptyFilter = {} // adjust to your filter shape if it needs explicit nulls per key
      setActionRequired(IMMEDIATE_ACTION.all)

      if (!isActiveTasks && isCardView) {
        resetForFilterChange()
        setFilterAndResetRefs(emptyFilter)
        setSelectedRefNums([])
        fetchData(null, true, isActiveTasks, isCardView, emptyFilter, [], true)
      } else {
        setFilterAndResetRefs(emptyFilter)
        setSelectedRefNums([])
      }
      setFilterResetLoading(false)
    }, 0)
  }, [
    activeFilterCount,
    isActiveTasks,
    isCardView,
    resetForFilterChange,
    setFilterAndResetRefs,
    setSelectedRefNums,
    setActionRequired,
    fetchData,
  ])

  // Task count pill 
  const taskCountPill = (
    <Typography
      variant="body2"
      sx={{
        fontSize: '13px',
        fontWeight: 700,
        color: '#6249d1',
        backgroundColor: '#F0EBFF',
        px: 1.5,
        py: 0.4,
        borderRadius: '20px',
        whiteSpace: 'nowrap',
      }}
    >
      {displayedTasks?.length === 0
        ? 'No Tasks'
        : displayedTasks?.length === 1
          ? '1 Task'
          : `${displayedTasks?.length} Tasks`}
    </Typography>
  )

  // Tablet view subheader
  const tabletSubheader = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
      {/* Row 1: ActivityFilter (left) | toggles + task count (right) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: 1,
        }}
      >
        {/* Left: Category / Process / Activity dropdowns */}
        <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
          {allTask?.length > 0 && activityFilterControl}
        </Box>

        {/* Right: toggles + task count pill */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          {taskCountPill}

          {/* Divider between pill and toggles */}
          <Box sx={{ width: '1px', height: '20px', backgroundColor: '#E1D9FB' }} />

          {togglesControl}
        </Box>
      </Box>

      {/* Row 2: Immediate action (left) | Ref number + tabs (right) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: 1,
        }}
      >
        {/* Left: Immediate action filter */}
        <Box sx={{ flex: '0 0 auto' }}>{immediateActionControl}</Box>

        {/* Right: Reference number + activity tabs */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexShrink: 0,
            ml: 'auto',
          }}
        >
          {activityTabsControl}
          {referenceNumberControl}
        </Box>
      </Box>
    </Box>
  )

  // Desktop subheader
  const desktopSubheader = (
    <Box
      sx={{
        ...cardIcons,
        justifyContent: 'space-between',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 0,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* LEFT: filters + task count */}
      <Box
        component="section"
        className="task-tab"
        sx={{ minWidth: 0, flex: '0 1 auto', maxWidth: '100%', boxSizing: 'border-box' }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            flexWrap: 'wrap',
            width: '100%',
            gap: '1rem',
          }}
        >
          {allTask?.length > 0 && (
            <>
              {activityFilterControl}
              <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                {immediateActionControl}
                {taskCountPill}
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* RIGHT: tabs + ref number + toggles */}
      <Box
        component="section"
        className="task-toggle d-flex"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 0,
          flexWrap: 'nowrap',
          width: 'auto',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        {activityTabsControl}
        <Box
          className="task-reference-number"
          sx={{ marginLeft: '-6px', width: 'auto', maxWidth: '100%', boxSizing: 'border-box' }}
        >
          {referenceNumberControl}
        </Box>
        {togglesControl}
      </Box>
    </Box>
  )

  return (
    <>
      <Container
        sx={{ flex: 1 }}
        className="main-container"
        style={{ padding: '4px 10px', margin: '2px auto' }}
      >
        <Box>
          <Box
            ref={subheaderRef}
            className="task-subheader"
            sx={{
              position: 'fixed',
              top: '48px',
              left: `${drawerOffset}px`,
              right: '1px',
              zIndex: 1,
              backgroundColor: '#fff',
              padding: { xs: '8px 12px', sm: '10px 14px' },
              borderBottom: '1px solid #F0EBFF',
              boxShadow: '0 1px 4px rgba(108,71,228,0.06)',
            }}
          >
            {isMobile ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '78%',
                  marginLeft: '60px',
                }}
              >
                {taskCountPill}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {allTask?.length > 0 && (
                    <Tooltip title="Apply/ Remove filters">
                      <IconButton
                        onClick={toggleFilterDrawer(true)}
                        aria-label="Open filters"
                        size="small"
                        sx={{ border: '1px solid #E1D9FB', borderRadius: '50%' }}
                      >
                        <Badge
                          badgeContent={activeFilterCount}
                          color="primary"
                          invisible={activeFilterCount === 0}
                        >
                          <FilterListIcon sx={{ color: '#6C47E4', fontSize: '20px' }} />
                        </Badge>
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            ) : isTablet ? (
              tabletSubheader
            ) : (
              desktopSubheader
            )}
          </Box>

          {/* Mobile filter drawer */}
          {isMobile && (
            <SwipeableDrawer
              anchor="top"
              open={filterDrawerOpen}
              onClose={toggleFilterDrawer(false)}
              onOpen={toggleFilterDrawer(true)}
              disableSwipeToOpen
              PaperProps={{
                sx: {
                  borderBottomLeftRadius: 16,
                  borderBottomRightRadius: 16,
                  maxHeight: '85vh',
                },
              }}
            >
              {/* Drawer header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: 2,
                  pt: 1,
                  marginTop: '44px',
                }}
              >
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#3D1FC4' }}>
                  Filters
                  {activeFilterCount > 0 && (
                    <Typography
                      component="span"
                      sx={{
                        ml: 1,
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#fff',
                        backgroundColor: '#6C47E4',
                        px: 0.8,
                        py: 0.2,
                        borderRadius: '10px',
                        verticalAlign: 'middle',
                      }}
                    >
                      {activeFilterCount}
                    </Typography>
                  )}
                </Typography>
                {activeFilterCount > 0 && (
                  <IconButton
                    size="small"
                    onClick={clearAllFilters}
                    aria-label="Clear all filters"
                    disabled={filterResetLoading}
                  >
                    {
                      <Tooltip title="Clear filters">
                        <FilterListOffOutlined sx={{ color: '#6C47E4', fontSize: '20px' }} />
                      </Tooltip>
                    }
                  </IconButton>
                )}
              </Box>

              {/* Filter controls */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  p: 2,
                  overflowY: 'auto',
                }}
              >
                {/* Toggles First */}
                {mobileTogglesControl}

                <Divider />

                {/* Filters */}
                {!isCardView && <Box>{activityTabsControl}</Box>}

                <Box>{activityFilterControl}</Box>

                {immediateActionControl}

                {referenceNumberControl}
              </Box>
              {/* Footer: Apply (or loader while applying) + drag handle */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  p: 2,
                  borderTop: '1px solid #F0EBFF',
                  position: 'sticky',
                  bottom: 0,
                  backgroundColor: '#fff',
                }}
              >
                {filterResetLoading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      py: 1.2,
                      borderRadius: '24px',
                      backgroundColor: '#F0EBFF',
                    }}
                  >
                    <CircularProgress size={24} sx={{ color: '#6C47E4' }} />
                  </Box>
                ) : (
                  <Box
                    component="button"
                    onClick={() => setFilterDrawerOpen(false)}
                    sx={{
                      flex: 1,
                      py: 1.2,
                      borderRadius: '24px',
                      border: 'none',
                      backgroundColor: '#6C47E4',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '14px',
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#5538C8' },
                    }}
                  >
                    Apply
                  </Box>
                )}
                <Box
                  sx={{
                    width: 36,
                    height: 4,
                    backgroundColor: 'grey.300',
                    borderRadius: 2,
                    mx: 'auto',
                    mt: 0.5,
                  }}
                />
              </Box>
            </SwipeableDrawer>
          )}

          {/* Main content */}
          {isMounting ||
          initialLoading ||
          tabSwitchLoading ||
          filterResetLoading ||
          (isLoading && !hasMore) ||
          (isLoading && !isCardView) ? (
            <Box
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '60vh',
              }}
            >
              <CircularProgress color="primary" />
            </Box>
          ) : (
            <Box style={{ marginTop: `${subheaderHeight + 10}px` }}>
              {displayedTasks?.length > 0 ? (
                <>
                  {isCardView ? (
                    <>
                      {immediateTasks.length > 0 && (
                        <MyTaskCardView
                          tasks={immediateTasks}
                          deleteTask={deleteTask}
                          setIsDelegateOpen={setIsDelegateOpen}
                          setTask={setTask}
                          setProcessReferenceNumber={setProcessReferenceNumber}
                          teamDetails={teamDetails}
                          breakpoints={BREAKPOINTS}
                          visibleData={visibleData}
                          onNavigate={onNavigate}
                        />
                      )}
                      {immediateTasks.length > 0 && nonImmediateTasks.length > 0 && (
                        <Divider style={{ margin: '20px 0px 32px 0px' }}>
                          <Typography
                            variant="h12"
                            align="center"
                            gutterBottom
                            style={{ fontSize: '13px', color: 'gray' }}
                          >
                            No immediate action required
                          </Typography>
                        </Divider>
                      )}
                      {nonImmediateTasks.length > 0 && (
                        <MyTaskCardView
                          tasks={nonImmediateTasks}
                          deleteTask={deleteTask}
                          setIsDelegateOpen={setIsDelegateOpen}
                          setTask={setTask}
                          setProcessReferenceNumber={setProcessReferenceNumber}
                          teamDetails={teamDetails}
                          breakpoints={BREAKPOINTS}
                          visibleData={visibleData}
                          onNavigate={onNavigate}
                        />
                      )}
                      {isLoading && hasMore && (
                        <Box
                          style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}
                        >
                          <CircularProgress color="primary" />
                        </Box>
                      )}
                      {hasMore && (
                        <div
                          ref={observerRef}
                          style={{ height: '10px', background: 'transparent' }}
                        />
                      )}
                    </>
                  ) : (
                    <MyTaskTableView
                      tasks={displayedTasks}
                      activityType={activityType}
                      setActivityType={setActivityType}
                      users={teamDetails}
                      onNavigate={onNavigate}
                    />
                  )}
                </>
              ) : (
                <Typography
                  variant="body1"
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '80vh',
                    width: '100%',
                  }}
                >
                  No {isActiveTasks ? 'active' : 'closed'} tasks found!
                </Typography>
              )}
            </Box>
          )}

          {isDelegateOpen && (
            <DelegateBox
              isDelegateOpen={isDelegateOpen}
              setIsDelegateOpen={setIsDelegateOpen}
              teamDetails={teamDetails}
              task={task}
              tasks={allTask}
              setTasks={setAllTask}
              processReferenceNumber={processReferenceNumber}
            />
          )}
        </Box>
      </Container>
    </>
  )
}
